import {
  AppSessionId,
  DisplayTableColumnId,
  Filter,
  HexVersionId,
  JsonSerializedParameterValueContents,
  ParameterOptions,
  SavedViewParamType,
  SharedFilterAutoLinks,
  SharedFilterId,
  SharedFilterManualLinks,
  SharedFilterPredicateOp,
  SharedFilterSessionStateId,
  UnaryColumnPredicateOpLiteral,
  buildParameterFilter,
  displayTableFilterToFilterCellFilter,
  notEmpty,
  parseJsonSerializedParameterValueContents,
  stableEmptyArray,
  stableEmptyObject,
  typedObjectEntries,
  typedObjectValues,
} from "@hex/common";
import { isEqual, memoize } from "lodash";
import microMemoize from "micro-memoize";

import {
  SharedFilterSessionStateMP,
  appSessionMPSelectors,
} from "../../../redux/slices/appSessionMPSlice.js";
import {
  SharedFilterMP,
  hexVersionMPSelectors,
} from "../../../redux/slices/hexVersionMPSlice.js";
import { savedViewDefaultAppSessionSelectors } from "../../../redux/slices/savedViewDefaultAppSessionSlice.js";
import { RootState } from "../../../redux/store.js";
import { createSelector } from "../../../redux/utils/index.js";
import { shallowEqual } from "../../../util/index.js";
import {
  getSqlFromFilter,
  getSqlPreviewFromFilterGroup,
  getTextFromElement,
} from "../../cell/renderers/filter/generateSqlPreview.js";
import { getDataframeColumnsForAutoLinks } from "../../shared-filter/sharedFilterLinkUtils.js";
import { getSharedFilterName } from "../../shared-filter/sharedFilterNameUtils.js";
import { getSharedFilterableDataFrameNamesForCell } from "../../shared-filter/sharedFilterUtils.js";
import {
  HumanReadableInputUpdate,
  getCacheKeyForSelector,
  selectSavedViewsEnabled,
} from "../useGetViewSelectors.js";

/**
 * Given a set of manual and auto links, as well as a potential filter name, computes a shared filter human readable name.
 * TODO: This may change; we may not need this function at all if we decided to not show the shared filter name in the dropdown and instead
 * just say something like "Shared filter for [X] cells". This unblocks us though and gets us started!
 */
export function getSharedFilterPreviewText({
  autoLinks,
  manualLinks,
  name: nameFromFilter,
  operator,
  value,
}: {
  name: string | null;
  autoLinks: SharedFilterAutoLinks | null;
  manualLinks: SharedFilterManualLinks | null;
  operator: SharedFilterPredicateOp | null;
  value: JsonSerializedParameterValueContents | null;
}): string {
  const name = getSharedFilterName({
    name: nameFromFilter,
    autoLinks,
    manualLinks,
  });
  const parsedValue =
    value != null ? parseJsonSerializedParameterValueContents(value) : null;
  if (
    value == null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0) ||
    // for unary filters, we don't apply the filter if the value is false,
    // which means that it won't generate a columnFilter, but that doesn't
    // mean that it's unconfigured!
    (UnaryColumnPredicateOpLiteral.guard(operator) && parsedValue === false)
  ) {
    return "Filter removed";
  }

  const columnFilter =
    operator != null
      ? buildParameterFilter({
          column: DisplayTableColumnId.check(name),
          operator: operator,
          value: parsedValue,
        })
      : null;

  if (columnFilter == null) {
    return "No filter applied";
  }

  const converted = displayTableFilterToFilterCellFilter(columnFilter);
  return getTextFromElement(
    Filter.guard(converted)
      ? getSqlFromFilter({
          filter: converted,
          position: [0],
        })
      : getSqlPreviewFromFilterGroup({
          group: converted,
          position: [0],
          topLevel: false,
          isTableDisplayFilter: true,
        }),
  );
}

type PersistedSharedFilterState = {
  name: string | null;
  manualLinks: null;
  value: JsonSerializedParameterValueContents | null;
  operator: SharedFilterPredicateOp | null;
  options: ParameterOptions | null;
  autoLinks: SharedFilterAutoLinks;
  id: SharedFilterId;
};

type PersistedChangedInputWithId = {
  currentContent: PersistedSharedFilterState | null;
  defaultContent: PersistedSharedFilterState;
  isChangedInCurrentSession: boolean;
  sharedFilterId: SharedFilterId;
};

type PersistedSharedInputWithCellInfo = {
  currentContent: PersistedSharedFilterState | null;
  defaultContent: PersistedSharedFilterState;
  sharedFilterId: SharedFilterId;
  isChangedInCurrentSession: boolean;
  cellLabels: string[];
};

type PersistedHumanReadableChangedInputWithId = {
  humanReadableInputUpdate: HumanReadableInputUpdate;
  sharedFilterId: SharedFilterId;
};

/**
 * A persisted shared filter is a type of shared filter originally generated from the published hex version; there is an associated shared_filter entity that is attached to the hex version.
 * When edited in an app session, a shared filter session state will be generated that references the persisted shared filter. This selector will return the changed shared fitlers, based on the aggregated results
 * for both the persisted filter value as well as the current app session's modifications.
 *
 * Note that a shared_filter_session_state for a persisted shared filter does not contain all of the contents from the shared filter; only the values that have been modified.
 **/
export const getPersistedSharedFilterSelectors = memoize(
  (hexVersionId: HexVersionId, appSessionId: AppSessionId) => {
    const selectSavedViewsEnabledInternal = (state: RootState): boolean =>
      selectSavedViewsEnabled(state, hexVersionId, appSessionId);

    /**
     * Memoized util method for persisted shared_filters that makes it easier in downstream code to combine shared filters with the session states that were not modified on the default app session.
     */
    const makePersistedSharedFilterStateFromSharedFilter = microMemoize(
      (sharedFilter: SharedFilterMP): PersistedSharedFilterState => ({
        ...sharedFilter,
        value: sharedFilter.defaultValue,
        manualLinks: null,
      }),
      {
        maxSize: 25, // smaller size since this method is only useful for the default app's shared_filter values.
      },
    );

    /**
     * Memoized util method that will combine the shared filter with the session state, prioritizing the session state values if they exist.
     */
    const makePersistedSharedFilterAggregatedWithSessionState = microMemoize(
      (
        sharedFilter: PersistedSharedFilterState,
        sessionState: SharedFilterSessionStateMP,
      ): PersistedSharedFilterState & {
        sharedFilterSessionStateId: SharedFilterSessionStateId;
      } => ({
        // Fields that always come from the shared filter
        id: sharedFilter.id,
        name: sharedFilter.name,
        manualLinks: null,
        autoLinks: sharedFilter.autoLinks ?? stableEmptyArray, // autolinks are not copied over to the session state ever.

        // Fields we should prioritize from the session state, unless they don't exist.
        value: sessionState.value ?? sharedFilter.value,
        operator: sessionState.operator ?? sharedFilter.operator,
        options: sessionState.options ?? sharedFilter.options,

        // Fields that always come from the session state
        sharedFilterSessionStateId: sessionState.id,
      }),
      {
        maxSize: 50, // larger size (2x above), since called for default app session and current app session
      },
    );

    const makeHumanReadablePersistedSharedFilterValues = microMemoize(
      ({
        cellLabels,
        currentContent,
        defaultContent,
        isChangedInCurrentSession,
      }: PersistedChangedInputWithId & {
        cellLabels: string[];
      }): PersistedHumanReadableChangedInputWithId => {
        return {
          sharedFilterId: defaultContent.id,
          humanReadableInputUpdate: {
            id: defaultContent.id,
            label: `Project filter for ${cellLabels.length} cell${cellLabels.length > 1 ? "s" : ""}`,
            humanReadableCurrentValue: currentContent
              ? getSharedFilterPreviewText({
                  operator: currentContent.operator,
                  value: currentContent.value,
                  autoLinks: currentContent.autoLinks,
                  manualLinks: null,
                  name: currentContent.name,
                })
              : null,
            humanReadableDefaultValue: getSharedFilterPreviewText({
              operator: defaultContent.operator,
              value: defaultContent.value,
              autoLinks: defaultContent.autoLinks,
              manualLinks: null,
              name: defaultContent.name,
            }),
            isChangedInCurrentSession: isChangedInCurrentSession,
            paramType: SavedViewParamType.SHARED_FILTER,
            tooltip: cellLabels.join(", "),
          },
        };
      },
      {
        maxSize: 25, // it is unlikely to have more than 25 shared filters on a published app; keep this cache small.
      },
    );

    const makeCellLabelsFromUsedDataframes = microMemoize(
      (
        cellLabels: string[],
        sharedFilterId: SharedFilterId,
        currentContent: PersistedSharedFilterState | null,
        defaultContent: PersistedSharedFilterState,
        isChangedInCurrentSession: boolean,
      ) => {
        return {
          cellLabels,
          currentContent,
          defaultContent,
          sharedFilterId,
          isChangedInCurrentSession,
        };
      },
      {
        maxSize: 25,
        // Perform a shallow equality comparison for the keys, so that we do not care about the order of the cellLabels.
        isMatchingKey: shallowEqual,
      },
    );

    /**
     * Selector that will get all of the persisted Shared Filters for the current hex version. Although typically better to avoid creating new objects when not necessary,
     *  this memoized map is helpful for @selectSavedViewPersistedSharedFilterStates since we directly use the shared_filter entities as PersistedSharedFilterStates.
     */
    const selectHexVersionPersistedSharedFilters = createSelector(
      [
        selectSavedViewsEnabledInternal,
        hexVersionMPSelectors.getSharedFilterSelectors(hexVersionId).selectAll,
      ],
      (savedViewsEnabled, sharedFilterMPSelectors) => {
        if (!savedViewsEnabled || sharedFilterMPSelectors == null) {
          return stableEmptyObject() as Record<
            SharedFilterId,
            PersistedSharedFilterState
          >;
        }

        return sharedFilterMPSelectors.reduce<
          Record<SharedFilterId, PersistedSharedFilterState>
        >((acc, sharedFilter) => {
          acc[sharedFilter.id] =
            makePersistedSharedFilterStateFromSharedFilter(sharedFilter);
          return acc;
        }, {});
      },
    );
    /**
     * Selector that will combine the saved view's app session shared filter session states with the persisted shared filters.
     */
    const selectDefaultSVPersistedSharedFilterStates = createSelector(
      [
        selectSavedViewsEnabledInternal,
        savedViewDefaultAppSessionSelectors.selectSharedFilterSessionStates,
        selectHexVersionPersistedSharedFilters,
      ],
      (
        savedViewsEnabled,
        sharedFilterSessionStates,
        persistedDefaultSharedFilters,
      ) => {
        const stableEmpty = stableEmptyObject() as Record<
          SharedFilterId,
          PersistedSharedFilterState
        >;
        if (!savedViewsEnabled) {
          return stableEmptyObject() as Record<
            SharedFilterId,
            PersistedSharedFilterState
          >;
        }

        const sharedFilterSessionStatesAndPersistedSharedFilters: Record<
          SharedFilterId,
          PersistedSharedFilterState
        > = {};

        // These do not need to be sorted since they are added to a map.
        for (const sharedFilterSessionState of sharedFilterSessionStates) {
          const { filterId } = sharedFilterSessionState;
          if (
            sharedFilterSessionState.deletedDate == null &&
            filterId != null &&
            persistedDefaultSharedFilters[filterId] &&
            sharedFilterSessionStatesAndPersistedSharedFilters[filterId] == null
          ) {
            sharedFilterSessionStatesAndPersistedSharedFilters[filterId] =
              makePersistedSharedFilterAggregatedWithSessionState(
                persistedDefaultSharedFilters[filterId],
                sharedFilterSessionState,
              );
          }
        }

        // Copy over any remaining that were not modified in the default app session
        for (const [sharedFilterId, sharedFilter] of typedObjectEntries(
          persistedDefaultSharedFilters,
        )) {
          if (
            sharedFilterSessionStatesAndPersistedSharedFilters[
              sharedFilterId
            ] == null
          ) {
            sharedFilterSessionStatesAndPersistedSharedFilters[sharedFilterId] =
              sharedFilter;
          }
        }

        return Object.keys(sharedFilterSessionStatesAndPersistedSharedFilters)
          .length === 0
          ? stableEmpty
          : sharedFilterSessionStatesAndPersistedSharedFilters;
      },
    );

    /**
     * Selector that will combine the current app session shared filter session states with the persisted shared filters for the hex version.
     */
    const selectCurrentAppSessionSharedFilterStates = createSelector(
      [
        selectSavedViewsEnabledInternal,
        appSessionMPSelectors.getSharedFilterSessionStateSelectors(appSessionId)
          .selectEntities,
        selectHexVersionPersistedSharedFilters,
      ],
      (
        savedViewsEnabled,
        sharedFilterSessionStates,
        persistedDefaultSharedFilters,
      ) => {
        const stableEmpty = stableEmptyObject() as Record<
          SharedFilterId,
          PersistedSharedFilterState & {
            sharedFilterSessionStateId: SharedFilterSessionStateId;
          }
        >;
        if (!savedViewsEnabled || sharedFilterSessionStates == null) {
          return stableEmpty;
        }

        const sharedFilterSessionStatesAndPersistedSharedFilters: Record<
          SharedFilterId,
          PersistedSharedFilterState & {
            sharedFilterSessionStateId: SharedFilterSessionStateId;
          }
        > = {}; // do not fill with the persisted filters; only add the persisted state to filter session states that have been modified.

        const sessionStateList = typedObjectValues(
          sharedFilterSessionStates,
        ).filter(notEmpty);
        for (const sharedFilterSessionState of sessionStateList) {
          const { filterId } = sharedFilterSessionState;
          if (
            sharedFilterSessionState.deletedDate == null &&
            filterId != null &&
            persistedDefaultSharedFilters[filterId] &&
            sharedFilterSessionStatesAndPersistedSharedFilters[filterId] == null
          ) {
            sharedFilterSessionStatesAndPersistedSharedFilters[filterId] =
              makePersistedSharedFilterAggregatedWithSessionState(
                persistedDefaultSharedFilters[filterId],
                sharedFilterSessionState,
              );
          }
        }
        // check the length of the record before returning for better perf.
        return Object.keys(sharedFilterSessionStatesAndPersistedSharedFilters)
          .length === 0
          ? stableEmpty
          : sharedFilterSessionStatesAndPersistedSharedFilters;
      },
    );

    /**
     * Selector to return the changed, persisted shared filter states in a human readable format.
     *
     * We create the human readable results in this selector, since this selector computes the human readable results differently
     * than manually linked shared filter session states.
     */
    const selectChangedPersistedSharedFilters = createSelector(
      [
        selectSavedViewsEnabledInternal,
        hexVersionMPSelectors.getCellContentAwareSelectors(hexVersionId)
          .selectCellIdToLabel,
        hexVersionMPSelectors.getCellContentSelectors(hexVersionId)
          .selectEntitiesByCellContentsId,
        selectCurrentAppSessionSharedFilterStates,
        selectDefaultSVPersistedSharedFilterStates,
      ],
      (
        savedViewsEnabled,
        cellIdToLabel,
        cellContents,
        currentSharedFilterSessionStates,
        savedViewDefaultSharedFilterSessionStates,
      ) => {
        if (!cellContents || !savedViewsEnabled) {
          return stableEmptyArray<PersistedSharedInputWithCellInfo>();
        }

        const changedSharedFilters: PersistedSharedInputWithCellInfo[] = [];
        for (const [sharedFilterId, currentContent] of typedObjectEntries(
          currentSharedFilterSessionStates,
        )) {
          const defaultSharedFilterSessionState =
            savedViewDefaultSharedFilterSessionStates[sharedFilterId];

          // For persisted shared filters, values are not copied over to the session state entity unless they have been changed;
          // A null value for a shared filter session state indicates that no changes have been made from the persisted shared filter state
          const isOperatorEqual =
            currentContent.operator == null ||
            isEqual(
              currentContent.operator,
              defaultSharedFilterSessionState.operator,
            );
          const isOptionsEqual =
            currentContent.options == null ||
            isEqual(
              currentContent.options,
              defaultSharedFilterSessionState.options,
            );
          const isValueEqual =
            currentContent.value == null ||
            isEqual(
              currentContent.value,
              defaultSharedFilterSessionState.value,
            );
          const isSharedFilterStateModified =
            !isOperatorEqual || !isOptionsEqual || !isValueEqual;

          const dataframeToColumns = getDataframeColumnsForAutoLinks(
            currentContent.autoLinks,
          );
          const cellLabels: string[] = [];
          for (const cellContent of typedObjectValues(cellContents)) {
            const usedDataFrames =
              cellContents != null
                ? getSharedFilterableDataFrameNamesForCell(cellContent).filter(
                    (df) => df in dataframeToColumns,
                  )
                : [];

            // If we found a used data frame, add the cell label to the finalized list of cellLabels impacted.
            if (usedDataFrames.length > 0) {
              const label = cellIdToLabel[cellContent.cellId];
              if (label != null) {
                cellLabels.push(label);
              }
            }
          }

          // Use a micromemoized method so that we keep referential identity of each changed shared filter.
          changedSharedFilters.push(
            makeCellLabelsFromUsedDataframes(
              cellLabels.sort(), // sort for improved cache hit rate. This micro memoized method uses a shallow equality comparison for the keys.
              sharedFilterId,
              currentContent,
              defaultSharedFilterSessionState,
              isSharedFilterStateModified,
            ),
          );
        }
        return changedSharedFilters;
      },
    );

    const selectChangedPersistedSFHumanReadableDiffValues: (
      state: RootState,
    ) => PersistedHumanReadableChangedInputWithId[] = createSelector(
      [selectChangedPersistedSharedFilters],
      (changedPersistedSharedFilters) => {
        return changedPersistedSharedFilters.map(
          makeHumanReadablePersistedSharedFilterValues,
        );
      },
    );

    const selectChangedSharedFilterIds: (
      state: RootState,
    ) => Set<SharedFilterId> = createSelector(
      [selectChangedPersistedSharedFilters],
      (changedPersistedSharedFilters) => {
        return new Set(
          changedPersistedSharedFilters.map(
            ({ sharedFilterId }) => sharedFilterId,
          ),
        );
      },
    );

    return {
      selectChangedSharedFilterIds,
      selectChangedPersistedSFHumanReadableDiffValues,
    };
  },
  (hexVersionId, appSessionId) =>
    getCacheKeyForSelector(hexVersionId, appSessionId),
);
